import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { parseDisplayNumericReading, type DossierEntry, type PropertyDossier } from '@immo/api-contract';
import { Button, InfoBox, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';
import { EMPTY, useDossierValueFormatter } from '@/pages/properties/dossier-value';

const NS = 'pages.admin.properties.detail.dossier';

/**
 * The Dossier tab's fetch state (IMM-549.2). `idle` = never opened — the tab is
 * lazy, so the property detail route loader never requests the dossier and the
 * seven other tabs never pay for it. A permission refusal (`forbidden`) is a
 * distinct state from a transient failure (`failed`): only the latter offers a
 * retry, because retrying a 403 can never succeed.
 */
export type DossierState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly dossier: PropertyDossier }
  | { readonly status: 'forbidden' }
  | { readonly status: 'failed' };

export type DossierController = {
  readonly state: DossierState;
  /** Called when the tab is first activated. A no-op once a fetch is in flight or done. */
  readonly open: () => void;
  /** Forces a refetch after a transient failure. */
  readonly retry: () => void;
};

/**
 * Owns the dossier's fetch-once-on-open lifecycle, lifted into the detail page so
 * it survives tab switches: the response is cached for the page's lifetime and
 * switching away and back issues no second request. `open()` is idempotent; only
 * `retry()` re-fetches. Every read re-composes the entries and may republish the
 * stale-brief event, so refetching on each tab flick would be pure noise.
 */
export function useDossierController(propertyId: string): DossierController {
  const [state, setState] = useState<DossierState>({ status: 'idle' });
  // A ref, not the state, gates concurrent fetches: `open()` may fire from a
  // render effect before the `loading` state has committed.
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;

    inFlight.current = true;
    setState({ status: 'loading' });

    try {
      const result = await apiClient.adminProperties.dossier({ pathParams: { id: propertyId } });

      if (result.ok) {
        setState({ status: 'loaded', dossier: result.body });
      } else if (result.status === 403) {
        setState({ status: 'forbidden' });
      } else {
        setState({ status: 'failed' });
      }
    } catch {
      setState({ status: 'failed' });
    } finally {
      inFlight.current = false;
    }
  }, [propertyId]);

  const open = useCallback(() => {
    setState((current) => {
      if (current.status === 'idle') {
        void load();

        return { status: 'loading' };
      }

      return current;
    });
  }, [load]);

  const retry = useCallback(() => {
    void load();
  }, [load]);

  return { state, open, retry };
}

type DossierTabProps = {
  readonly state: DossierState;
  readonly onRetry: () => void;
};

/**
 * Renders the dossier read-only: the cached brief above every taxonomy key the
 * property has data for, each key's confirmed Objekt value above the extracted
 * facts behind it, with contested keys marked and every competing value kept
 * visible. Nothing here writes — there is no control that issues a mutation.
 */
export function DossierTab({ state, onRetry }: DossierTabProps): ReactNode {
  const t = useTranslations(NS);

  if (state.status === 'idle' || state.status === 'loading') {
    return <p className="py-10 text-center text-base-sm text-text-tertiary">{t('loading')}</p>;
  }

  if (state.status === 'forbidden') {
    // No retry: a refusal can never succeed on retry (parent Resolved Decision 7).
    return (
      <div className="rounded-md border border-neutral-200 bg-canvas px-4 py-10 text-center text-base-sm text-text-secondary">
        {t('forbidden')}
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-neutral-200 bg-canvas px-4 py-10 text-center">
        <p className="text-base-sm text-text-secondary">{t('error')}</p>
        <Button variant="secondary" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  return <DossierView dossier={state.dossier} />;
}

/**
 * The loaded dossier with its two client-side filters (IMM-549.3): a text search
 * on key/label and a „Nur Widersprüche" toggle. Both narrow the same already-fetched
 * list — no filter interaction issues a request — and they combine with AND. The
 * brief is never filtered: it always describes the whole property.
 */
function DossierView({ dossier }: { dossier: PropertyDossier }): ReactNode {
  const t = useTranslations(NS);
  const [term, setTerm] = useState('');
  const [onlyConflicts, setOnlyConflicts] = useState(false);

  // Taken from the FULL dossier, not the filtered view — the control reports what
  // is there, not what survives the text filter. A count that changed as you typed
  // would be unreadable, and with zero conflicts the toggle is disabled, not hidden.
  const conflictCount = dossier.entries.filter((entry) => entry.conflict).length;

  const needle = term.trim().toLowerCase();
  const visible = dossier.entries.filter((entry) => {
    if (onlyConflicts && !entry.conflict) return false;

    if (needle.length === 0) return true;

    return entry.factKey.toLowerCase().includes(needle) || entry.label.toLowerCase().includes(needle);
  });

  const groups = groupEntries(visible);

  return (
    <div className="space-y-6">
      <Brief brief={dossier.brief} />
      {/* The server decides whether anything was extracted (IMM-654) — never
          inferred here from the entry list, which always carries the Objekt's
          own confirmed fields. The notice sits ABOVE the entries and replaces
          nothing: 'no facts detected' and 'here are the confirmed values' are
          both true at once. */}
      {!dossier.factsDetected && <InfoBox type="info">{t('no-facts-detected')}</InfoBox>}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={t('search-placeholder')}
          aria-label={t('search-placeholder')}
          className="h-8 w-64 rounded-sm border border-border bg-surface px-2 text-base-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:shadow-focus"
        />
        <button
          type="button"
          onClick={() => setOnlyConflicts((on) => !on)}
          disabled={conflictCount === 0}
          aria-pressed={onlyConflicts}
          className={`inline-flex h-7 items-center rounded-sm border px-2 text-base-sm transition-colors focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50 ${
            onlyConflicts
              ? 'border-border-strong bg-surface font-medium text-text-primary'
              : 'border-border bg-surface text-text-secondary hover:bg-hover-bg'
          }`}
        >
          {t('only-conflicts', { count: conflictCount })}
        </button>
      </div>
      {groups.length === 0 ? (
        <p className="py-8 text-center text-base-sm text-text-tertiary">{t('no-match')}</p>
      ) : (
        groups.map(({ scope, entries }) => (
          <section key={scope} className="space-y-3">
            <h3 className="text-base font-semibold text-text-primary">{t(`groups.${scope}`)}</h3>
            <div className="space-y-4">
              {entries.map((entry) => (
                <EntryCard key={entry.factKey} entry={entry} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function Brief({ brief }: { brief: PropertyDossier['brief'] }): ReactNode {
  const t = useTranslations(NS);
  const intl = useIntl();

  if (brief === null) {
    return (
      <section className="rounded-md border border-neutral-200 bg-canvas px-4 py-3">
        <h3 className="text-base font-semibold text-text-primary">{t('brief.heading')}</h3>
        <p className="mt-1 text-base-sm text-text-tertiary">{t('brief.empty')}</p>
      </section>
    );
  }

  return (
    <section className="rounded-md border border-neutral-200 bg-canvas px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-text-primary">{t('brief.heading')}</h3>
        <span className="text-xs text-text-tertiary">
          {t('brief.created-at', { date: intl.formatDate(brief.generatedAt, { dateStyle: 'medium' }) })}
        </span>
      </div>
      <p className="mt-2 whitespace-pre-line text-base-sm text-text-secondary">{brief.text}</p>
      {brief.stale && (
        <div className="mt-3">
          <InfoBox type="warning">{t('brief.stale')}</InfoBox>
        </div>
      )}
    </section>
  );
}

function EntryCard({ entry }: { entry: DossierEntry }): ReactNode {
  const t = useTranslations(NS);
  const formatValue = useDossierValueFormatter();

  return (
    <div className="rounded-md border border-neutral-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-4 py-2">
        <span className="text-base-sm font-medium text-text-primary">{entry.label}</span>
        {entry.publicSafe && (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-text-tertiary">{t('public-safe')}</span>
        )}
        {entry.conflict && (
          <span className="rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700">
            {t('conflict')}
          </span>
        )}
      </div>
      <div className="space-y-3 px-4 py-3">
        {entry.authoritative !== null && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">{t('confirmed-value')}</div>
            <div className="mt-0.5 text-base-sm text-text-primary">{formatValue(entry, entry.authoritative.value)}</div>
          </div>
        )}
        {entry.advisory.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-text-tertiary">
              {t('extracted-values')}
            </div>
            <FactsTable entry={entry} />
          </div>
        )}
      </div>
    </div>
  );
}

type Fact = DossierEntry['advisory'][number];
type FactField = 'feature' | 'source' | 'confidence' | 'extractedAt';

// A fact with its cell text and its typed sort value resolved, so the sort key stays module-level.
type FactRow = { readonly fact: Fact; readonly value: string; readonly featureSort: SortValue };

const FACT_HEADS = [
  { field: 'feature', labelKey: 'feature', className: undefined },
  { field: 'source', labelKey: 'source', className: undefined },
  { field: 'confidence', labelKey: 'confidence', className: 'w-[120px]' },
  { field: 'extractedAt', labelKey: 'extracted-at', className: 'w-[140px]' },
] as const;

function factSortKey(row: FactRow, field: FactField): SortValue {
  switch (field) {
    case 'feature':
      return row.featureSort;
    case 'source':
      return row.fact.sourceName;
    case 'confidence':
      return row.fact.confidence;
    case 'extractedAt':
      return Date.parse(row.fact.extractedAt);
  }
}

// Numbers and dates sort by what they are, not by their German text: „1.200 m²" is not before „85 m²".
function featureSortValue(entry: DossierEntry, fact: Fact, value: string): SortValue {
  if (entry.valueType === 'number') {
    const numeric = parseDisplayNumericReading(fact.value);

    if (numeric !== null) return numeric;
  }

  if (entry.valueType === 'date' && typeof fact.value === 'string') {
    // Same local-midnight rule as the formatter, so the sort and the display agree about the day.
    const parsed = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(fact.value) ? `${fact.value}T00:00:00` : fact.value);

    if (Number.isFinite(parsed)) return parsed;
  }

  return value === EMPTY ? null : value;
}

function FactsTable({ entry }: { entry: DossierEntry }): ReactNode {
  const t = useTranslations(NS);
  const tTable = useTranslations('pages.admin.shell.table');
  const tCategory = useTranslations(`${NS}.category`);
  const intl = useIntl();
  const formatValue = useDossierValueFormatter();
  const factRows = useMemo(
    () =>
      entry.advisory.map((fact): FactRow => {
        const value = formatValue(entry, fact.value, fact.unit);

        return { fact, value, featureSort: featureSortValue(entry, fact, value) };
      }),
    [entry, formatValue],
  );
  const { sorted, sort, toggleSort } = useTableSort(factRows, factSortKey);

  return (
    <Table flush>
      <TableHeader>
        <TableRow>
          {FACT_HEADS.map((head) => (
            <TableHead
              key={head.field}
              className={head.className}
              {...(sort !== null && sort.field === head.field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
            >
              <SortableHeader
                field={head.field}
                label={t(`columns.${head.labelKey}`)}
                ariaLabel={tTable('sort-label', { column: t(`columns.${head.labelKey}`) })}
                sort={sort}
                onSort={toggleSort}
              />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {/* Default order is the API's: newest extraction first. A chosen sort reorders
            by that column, never by confidence alone; confidence never greys or hides
            a fact — every reading stays equally visible (AC). */}
        {sorted.map(({ fact, value }) => (
          <TableRow key={fact.factId}>
            <TableCell className="text-text-primary">{value}</TableCell>
            <TableCell className="text-text-secondary">
              {/* Plain text — never a link or button. A null sourceName means the
                  document was deleted since extraction, or the fact is an agent's DOCUMENT finding; the sourceId is never shown. */}
              {fact.sourceName === null ? (
                t('source-unavailable')
              ) : (
                <span>
                  {fact.sourceName}
                  {fact.sourceCategory !== null && ` · ${tCategory(fact.sourceCategory)}`}
                </span>
              )}
              <span className="block text-xs text-text-tertiary">{t(`modality.${fact.modality}`)}</span>
            </TableCell>
            <TableCell className="text-text-secondary">{Math.round(fact.confidence * 100)}%</TableCell>
            <TableCell className="text-text-secondary">
              {intl.formatDate(fact.extractedAt, { dateStyle: 'medium' })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const SCOPE_ORDER = ['PROPERTY_INTRINSIC', 'DEAL_CONTEXT'] as const;

type ScopeGroup = { readonly scope: (typeof SCOPE_ORDER)[number]; readonly entries: ReadonlyArray<DossierEntry> };

/**
 * Groups entries „Objektmerkmale" (PROPERTY_INTRINSIC) first, then „Deal-Kontext",
 * each sorted alphabetically by label. The API decides which keys exist; the
 * screen only decides their order and never invents a placeholder.
 */
function groupEntries(entries: ReadonlyArray<DossierEntry>): ReadonlyArray<ScopeGroup> {
  return SCOPE_ORDER.map((scope) => ({
    scope,
    entries: entries
      .filter((entry) => entry.scope === scope)
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label, 'de')),
  })).filter((group) => group.entries.length > 0);
}
